import { TASK_STATUS_LABELS, type TaskStatus } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { AufgabenTitel } from '../components/AufgabeKontext'
import { Topbar } from '../components/Topbar'
import { VorhabenDetail } from '../components/VorhabenDetail'
import { WartendeAbnahmen } from '../components/WartendeAbnahmen'
import { Card, CardPad, PrioBadge, SourceBadge } from '../components/ui/primitives'
import { aufgabenQuery, vorhabenAllerWeltenQuery } from '../lib/queries'

/**
 * **Die einzige Ansicht eines Gastes** (18.08.2026,
 * [E-130](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * ## Warum sie so wenig kann
 *
 * Weil sie so wenig können soll. Ein Gast ist kein Teammitglied: er wurde zu einzelnen
 * Aufgaben hinzugezogen – erwähnt, gefragt, oder er hat selbst etwas gemeldet. Was er
 * braucht, ist die Liste genau dieser Aufgaben und ein Weg hinein. Kein Filter, keine
 * Sortierung, keine Sammelaktion: bei drei Zeilen ist ein Filter Zierrat, und bei dreißig
 * hätte man ein anderes Problem.
 *
 * ## Warum hier nicht gefiltert wird
 *
 * Die Liste ist der **ganze** Bestand, den dieser Anmeldung zusteht – der Server hat schon
 * gefiltert (`aufgabenSichtbarkeit` in `apps/api/src/trpc/rechte.ts`). Hier noch einmal zu
 * filtern hieße, dieselbe Regel an zwei Stellen zu führen, und die zweite wäre die, die
 * irgendwann abweicht.
 */
export function Beteiligt() {
  const { data: aufgaben = [], isLoading } = useQuery(aufgabenQuery)
  /**
   * **Vorhaben, zu denen ich eingeladen bin** ([E-440](../../../../docs/ENTSCHEIDUNGEN.md#e-440)).
   * Der Server liefert einem Gast nur diese – derselbe Grund wie oben, hier nicht zu filtern.
   */
  const { data: vorhaben = [] } = useQuery(vorhabenAllerWeltenQuery)
  const [vorhabenOffen, setVorhabenOffen] = useState<string | null>(null)

  /** `?vorhaben=` aus der Einladungsmail öffnet das Vorhaben – einmal beim Laden, wie in `Vorhaben.tsx`. */
  useEffect(() => {
    const gewuenscht = new URLSearchParams(window.location.search).get('vorhaben')
    if (gewuenscht) setVorhabenOffen(gewuenscht)
  }, [])

  /**
   * **`verworfen` gibt es bei Aufgaben nicht** – das ist ein Zustand von *Anfragen*
   * ([E-99](../../../../docs/ENTSCHEIDUNGEN.md)). Der Statusfluss einer Aufgabe endet bei
   * `erledigt` oder `zurueckgestellt`; die beiden Prüfungen darauf waren wirkungslos und
   * hätten bei einer späteren Umbenennung falsche Sicherheit gegeben.
   */
  const offen = aufgaben.filter((a) => a.status !== 'erledigt' && a.status !== 'zurueckgestellt')
  const erledigt = aufgaben.filter((a) => a.status === 'erledigt' || a.status === 'zurueckgestellt')

  return (
    <>
      <Topbar
        titel="Meine Beteiligungen"
        untertitel={
          isLoading
            ? 'lädt …'
            : aufgaben.length === 0
              ? 'noch nichts'
              : `${offen.length} offen${erledigt.length > 0 ? `, ${erledigt.length} abgeschlossen` : ''}`
        }
      />

      <p className="mb-3 text-[12.5px] text-ink2">
        Hier stehen die Aufgaben, an denen du beteiligt bist: in denen dich jemand mit @ erwähnt
        hat, in denen du kommentiert hast, die du selbst eingestellt hast oder die auf deine Abnahme
        warten. Du kannst sie lesen, darin antworten und abnehmen, was auf dich wartet.
      </p>

      {/**
       * **Was auf meine Abnahme wartet, steht oben** (16.09.2026,
       * [E-355](../../../../docs/ENTSCHEIDUNGEN.md#e-355)). Derselbe Baustein wie in „Mein
       * Bereich"; der Server gibt einem Gast nur die Abnahmen, die auf ihn selbst warten. Ohne
       * Zeilen zeigt er nichts – die meisten Gäste nehmen nie ab, und eine leere Überschrift
       * wäre eine Frage, die niemand gestellt hat.
       */}
      <WartendeAbnahmen />

      {vorhaben.length > 0 && (
        <>
          <h3 className="mt-1 mb-1.5 text-xs font-bold text-ink2">
            Vorhaben, zu denen du eingeladen bist
          </h3>
          <Card className="mb-4">
            <CardPad className="py-1">
              {vorhaben.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVorhabenOffen(v.id)}
                  className="flex w-full cursor-pointer flex-wrap items-baseline gap-2 border-b border-line py-2 text-left last:border-b-0 hover:bg-[#f7f6f3]"
                >
                  <span className="text-[13px] font-semibold">{v.label}</span>
                  <span className="ml-auto text-[11px] text-muted">
                    {v.beschreibung ? '' : 'noch nicht beschrieben · '}
                    {v.stand.gesamt === 0
                      ? 'noch keine Aufgaben'
                      : `${v.stand.offen} offen von ${v.stand.gesamt}`}
                  </span>
                </button>
              ))}
            </CardPad>
          </Card>
        </>
      )}

      {vorhabenOffen && (
        <VorhabenDetail vorhabenId={vorhabenOffen} schliessen={() => setVorhabenOffen(null)} />
      )}

      {!isLoading && aufgaben.length === 0 && vorhaben.length === 0 && (
        <Card>
          <CardPad>
            <p className="text-[13px] text-muted">
              Noch nichts. Sobald dich jemand in einem Kommentar mit @ anspricht, erscheint die
              Aufgabe hier – und du bekommst eine Mail.
            </p>
          </CardPad>
        </Card>
      )}

      {offen.length > 0 && <Liste aufgaben={offen} />}

      {erledigt.length > 0 && (
        <>
          <h3 className="mt-5 mb-1.5 text-xs font-bold text-ink2">Abgeschlossen</h3>
          <Liste aufgaben={erledigt} blass />
        </>
      )}
    </>
  )
}

function Liste({
  aufgaben,
  blass = false,
}: {
  aufgaben: {
    id: string
    title: string
    status: string
    priority: string | null
    sourceId: string
    assigneeName: string | null
    letztesEreignisAm: string | null
  }[]
  blass?: boolean
}) {
  return (
    <Card className={blass ? 'opacity-70' : ''}>
      <CardPad className="py-1">
        {aufgaben.map((a) => (
          <div
            key={a.id}
            className="flex flex-wrap items-baseline gap-2 border-b border-line py-2 last:border-b-0"
          >
            <SourceBadge source={a.sourceId} />
            <AufgabenTitel aufgabeId={a.id} titel={a.title} className="text-[13px] font-semibold" />
            {a.priority && <PrioBadge priority={a.priority as 'A'} />}
            <span className="ml-auto flex items-baseline gap-2 text-[11px] text-muted">
              <span>{TASK_STATUS_LABELS[a.status as TaskStatus] ?? a.status}</span>
              {/**
               * **Der Bearbeiter steht dabei** – für einen Hinzugezogenen ist das die
               * eigentliche Auskunft: *wer kümmert sich?* Ohne ihn bliebe nur ein Status,
               * und „in Arbeit" beantwortet die Frage nicht.
               */}
              {a.assigneeName && <span>· {a.assigneeName}</span>}
              {a.letztesEreignisAm && (
                <span>
                  · zuletzt{' '}
                  {new Date(a.letztesEreignisAm).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                  })}
                </span>
              )}
            </span>
          </div>
        ))}
      </CardPad>
    </Card>
  )
}
